import React, { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import styled from 'styled-components';
import { Button, H4, LoaderSpin, ModalFooter, ModalHeader, ModalLayout, ModalTitle, Text } from 'components';
import { lineHeight, theme, toRem } from 'design';
import { IPayout } from 'types/Payout';
import { centsToDollarsDisplay, formatDateForUI } from 'utils';

const StyledModalLayout = styled(ModalLayout)`
  padding: ${toRem(theme.space['3xl'])} ${toRem(theme.space.lg)};
`;

const Content = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: ${toRem(theme.space.xl)};

  .modal-header {
    display: flex;
    flex-flow: column nowrap;
    gap: ${toRem(theme.space.md)};

    p {
      font-size: ${toRem(theme.font.size.md)};
      line-height: ${lineHeight(theme.font.size.md)};
    }
  }
`;

const DetailsContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  background-color: ${theme.color.neutral[50]};
  border-radius: ${toRem(8)};
  padding: ${toRem(theme.space.lg)};
  gap: ${toRem(theme.space.lg)};
  width: 100%;
`;

const HelpList = styled.ul<{ $hideBullets?: boolean }>`
  display: flex;
  flex-flow: column nowrap;
  list-style: ${({ $hideBullets }) => ($hideBullets ? 'none' : 'disc inside')};
  gap: ${toRem(theme.space.xs)};

  p {
    font-size: ${toRem(theme.font.size.md)};
    line-height: ${lineHeight(theme.font.size.md)};
  }
`;

const HelpItem = styled.li<{ $hideBullets?: boolean }>`
  font-size: ${toRem(theme.font.size.xs)};
  color: ${theme.color.neutral[500]};
  padding-left: ${({ $hideBullets }) => ($hideBullets ? 0 : toRem(theme.space.sm))};

  a {
    font-size: ${toRem(theme.font.size.xs)};
    line-height: ${lineHeight(theme.font.size.xs)};
    color: ${theme.color.primary[500]};
    text-decoration: none;
  }
`;

const PaymentsList = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: ${toRem(theme.space.sm)};
  width: 100%;
`;

const GridLayout = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: ${toRem(theme.space.xs)};

  &:not(:last-child) {
    border-bottom: 1px solid ${theme.color.neutral[200]};
    padding-bottom: ${toRem(theme.space.sm)};
  }
`;

const GridHeader = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: ${toRem(theme.space.xs)};

  h4,
  span {
    font-size: ${toRem(theme.font.size.xs)};
    line-height: ${lineHeight(theme.font.size.xs)};
  }

  span {
    font-weight: ${theme.font.weight.regular};
  }
`;

const GridItem = styled.div`
  display: grid;
  grid-template-columns: 1fr; // stack on screens < 400px
  width: 100%;
  align-items: center;

  p {
    font-size: ${toRem(theme.font.size.xs)};
    line-height: ${lineHeight(theme.font.size.xs)};
    color: ${theme.color.neutral[500]};
    white-space: nowrap;
  }

  &.expandable {
    cursor: pointer;
  }

  // show 3 columns on screens >= 400px
  @media (min-width: ${theme.breakpoint.xsmall}px) {
    grid-template-columns: 2fr 1fr 1fr;
    gap: ${toRem(theme.space.sm)};

    p {
      justify-self: start;
      &:not(:first-child) {
        justify-self: end;
      }
    }
  }
`;

const AccountHealthPayoutsList = ({ payouts, dataCy }: { payouts: IPayout[]; dataCy: string }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const sortedPayouts = useMemo(() => [...payouts].sort((a: IPayout, b: IPayout) => dayjs(a.paymentDate).diff(dayjs(b.paymentDate))), [payouts]);
  const initialPayouts = sortedPayouts.slice(0, 3); // show the first 3 payouts and hide the rest
  const remainingPayouts = sortedPayouts.slice(3); // the remaining payouts to reveal

  if (payouts.length === 0) return null;

  const renderPayouts = (payoutsToRender: IPayout[]) =>
    payoutsToRender.map((payout) => (
      <GridItem key={payout.id} data-cy={`${dataCy}-payouts-list-item`}>
        <Text>ID: {payout.id}</Text>
        <Text>{formatDateForUI(payout.paymentDate)}</Text>
        <Text>{centsToDollarsDisplay(payout.gross)}</Text>
      </GridItem>
    ));

  return (
    <GridLayout data-cy={`${dataCy}-payouts-list`}>
      <GridHeader data-cy={`${dataCy}-payouts-list-header`}>
        <H4 fontWeight="semibold" allowWordWrap>
          {payouts[0].site.name} <span>({payouts.length})</span>
        </H4>
      </GridHeader>
      {renderPayouts(initialPayouts)}
      {remainingPayouts.length > 0 ? (
        !isExpanded ? (
          <GridItem className="expandable" onClick={() => setIsExpanded(!isExpanded)} data-cy={`${dataCy}-payouts-list-item-expandable`}>
            <Text>+ {remainingPayouts.length} more</Text>
          </GridItem>
        ) : (
          renderPayouts(remainingPayouts)
        )
      ) : null}
    </GridLayout>
  );
};

export interface IAccountHealthModalConfig {
  modalTitle: string;
  modalSubtitle: string;
  modalFooter?: React.ReactNode;
  helpListTitle: string;
  helpListItems: string[] | React.ReactNode[];
  hideHelpListBullets?: boolean;
}

interface AccountHealthModalProps {
  isActive: boolean;
  isLoading?: boolean;
  onClose: () => void;
  dataCy?: string;
  config: IAccountHealthModalConfig;
  payouts: Record<string, IPayout[]>;
}

export const AccountHealth = ({ isActive, onClose, dataCy = 'account-health', config, payouts, isLoading }: AccountHealthModalProps) => {
  const { modalTitle, modalSubtitle, modalFooter, helpListTitle, helpListItems, hideHelpListBullets } = config;

  return (
    <StyledModalLayout isActive={isActive} onClose={onClose} hideCloseButton data-cy={`${dataCy}-modal`}>
      <Content>
        <ModalHeader className="modal-header">
          <ModalTitle>{modalTitle}</ModalTitle>
          <Text style={{ color: theme.color.neutral[500] }} data-cy={`${dataCy}-description`}>
            {modalSubtitle}
          </Text>
        </ModalHeader>
        <DetailsContainer>
          <HelpList $hideBullets={hideHelpListBullets} data-cy={`${dataCy}-help-list`}>
            <Text data-cy={`${dataCy}-help-list-title`}>{helpListTitle}</Text>
            {helpListItems.map((item, index) => (
              <HelpItem key={index} $hideBullets={hideHelpListBullets} data-cy={`${dataCy}-help-item`}>
                {item}
              </HelpItem>
            ))}
          </HelpList>
          <PaymentsList>{isLoading ? <LoaderSpin color={theme.color.neutral[200]} /> : Object.entries(payouts).map(([siteId, sitePayouts]) => <AccountHealthPayoutsList key={siteId} payouts={sitePayouts} dataCy={dataCy} />)}</PaymentsList>
        </DetailsContainer>
      </Content>
      {modalFooter || (
        <ModalFooter>
          <Button label="Close" onClick={onClose} appearance="white" data-cy={`${dataCy}-close-button`} />
        </ModalFooter>
      )}
    </StyledModalLayout>
  );
};
